import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useRouteMatch } from 'react-router'
import { getMerchandiseCategorySearchUrl, getMerchandiseBrandSearchUrl } from '../merchandiseUtils'
import MerchandiseSearchPage from '../MerchandiseSearchPage/MerchandiseSearchPage'
import MerchandiseSearchPageNoResults, { FullWidthContainer } from '../MerchandiseSearchPage/MerchandiseSearchPageNoResults'
import MerchandiseBreadcrumbsSection from '../MerchandiseBreadcrumbsSection'
import MerchandiseOfferCarousel from '../MerchandiseCarousels/MerchandiseOfferCarousel'
import MerchandiseCategoriesCarousel from '../MerchandiseCarousels/MerchandiseCategoriesCarousel'
import MerchandiseBrandsCarousel from '../MerchandiseCarousels/MerchandiseBrandsCarousel'
import useMerchandiseBrandsList from 'hooks/Merchandise/useMerchandiseBrandsList'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import MerchandiseCategoryPageHero from './MerchandiseCategoryPageHero'

function MerchandiseCategoryPage() {
  const intl = useIntl()
  const { categorySlug } = useRouteMatch<{ categorySlug: string }>().params
  const trackingPageName = 'Category Page'

  const [category, fetchingCategory] = useMerchandiseCategory({ slug: categorySlug })

  // Call all hooks unconditionally (Rules of Hooks)
  const categoryFilters = useMemo((): App.MerchandiseFilters | undefined => {
    if (category) {
      return {
        categories: [category.id],
      }
    }
  }, [category])

  // Select best 2 subcategories based on advertCount
  // Logic: Show first 2 with ≥4 products. If < 2 found, fallback to first 2 anyway.
  const childCategories = useMemo(() => {
    const allChildren = category?.childCategories ?? []
    if (allChildren.length === 0) return []

    // Find subcategories with ≥4 products
    const categoriesWithProducts = allChildren.filter(child => (child.advertCount ?? 0) >= 4)

    if (categoriesWithProducts.length >= 2) {
      // Case 1: Found 2+ with ≥4 products → show first 2
      return categoriesWithProducts.slice(0, 2)
    } else if (categoriesWithProducts.length === 1) {
      // Case 2: Found only 1 with ≥4 products → show that one + first overall
      const firstCategory = categoriesWithProducts[0]
      const firstOverall = allChildren[0]
      // If they're the same, add the second overall category
      if (firstCategory.id === firstOverall.id) {
        return allChildren.slice(0, 2)
      }
      return [firstCategory, firstOverall]
    } else {
      // Case 3: Found 0 with ≥4 products → fallback to first 2
      return allChildren.slice(0, 2)
    }
  }, [category])

  const childCategoriesFilters = useMemo((): Array<{ category: App.MerchandiseCategory, filters: App.MerchandiseFilters }> => {
    return childCategories.map(category => {
      return {
        category,
        filters: {
          categories: [category.id],
        },
      }
    })
  }, [childCategories])

  const brandFilters = useMemo((): App.MerchandiseBrandFilters | undefined => {
    if (category) {
      return {
        categories: [category.id],
      }
    }
  }, [category])
  const [brandsList] = useMerchandiseBrandsList({ categories: [category?.id ?? ''] }, { disabled: !category })
  const [featuredBrand] = useMerchandiseBrand(brandsList[0])

  const featuredBrandOffersFilter = useMemo((): App.MerchandiseFilters | undefined => {
    if (featuredBrand) {
      return {
        ...(featuredBrand ? { brands: [featuredBrand.id] } : {}),
        ...(category ? { categories: [category.id] } : {}),
      }
    }
  }, [featuredBrand, category])

  // For subcategories (level > 1), render search page instead of category page
  if (category && category.level > 1) {
    return <MerchandiseSearchPage />
  }

  // Show loading indicator while fetching
  if (fetchingCategory) {
    return <LoadingIndicator />
  }

  // If category doesn't exist after loading completes, show no results page
  if (!category) {
    return <>
      <FullWidthContainer>
        <MerchandiseBreadcrumbsSection
          breadcrumbs={[{
            label: intl.formatMessage({
              id: 'f4uOmz-BWhqSUMOWSDoj',
              defaultMessage: 'Search results',
              description: 'Breadcrumb label for search results when category not found',
            }),
          }]}
          loading={false}
        />
      </FullWidthContainer>
      <MerchandiseSearchPageNoResults
        category={undefined}
        brand={undefined}
        search={undefined}
        showBreadcrumbs={false}
      />
    </>
  }

  // childCategories now contains exactly the right 2 subcategories (logic handled above)
  const [firstChild, secondChild] = childCategoriesFilters

  const childCategoryElements = [
    ...(firstChild ? [
      <MerchandiseOfferCarousel
        key={firstChild.category.id}
        title={
          <FormattedMessage
            id="oxcEJf8KCsXQuVZ0VxC5"
            defaultMessage="Best of <em>{categoryName}</em>"
            description="Heading title for best products in a subcategory carousel"
            values={{
              categoryName: firstChild.category.name,
              em: (chunks) => <em>{chunks}</em>,
            }}
          />
        }
        filters={firstChild.filters}
        viewAllTo={getMerchandiseCategorySearchUrl(firstChild.category)}
        trackingListName={`Best of ${firstChild.category.name}`}
        trackingPageName={trackingPageName}
      />,
    ] : []),
    ...(featuredBrand ? [
      <MerchandiseOfferCarousel
        key={`featured-${featuredBrand.id}`}
        title={
          <FormattedMessage
            id="BP6soVQh6hi-i0DPKzr8"
            defaultMessage="Shop the latest from <em>{brandName}</em>"
            description="Heading title for featured brand latest products carousel"
            values={{
              brandName: featuredBrand.name,
              em: (chunks) => <em>{chunks}</em>,
            }}
          />
        }
        filters={featuredBrandOffersFilter}
        viewAllTo={getMerchandiseBrandSearchUrl(featuredBrand)}
        trackingListName={`Shop the latest from ${featuredBrand.name}`}
        trackingPageName={trackingPageName}
      />,
    ] : []),
    ...(secondChild ? [
      <MerchandiseOfferCarousel
        key={secondChild.category.id}
        title={
          <FormattedMessage
            id="Ey5cL7HRZi_0BNVRqhjs"
            defaultMessage="Discover more in <em>{categoryName}</em>"
            description="Heading title for second subcategory products carousel"
            values={{
              categoryName: secondChild.category.name,
              em: (chunks) => <em>{chunks}</em>,
            }}
          />
        }
        filters={secondChild.filters}
        viewAllTo={getMerchandiseCategorySearchUrl(secondChild.category)}
        trackingListName={`Discover more in ${secondChild.category.name}`}
        trackingPageName={trackingPageName}
      />,
    ] : []),
  ]

  return <div>
    {category.image && <MerchandiseCategoryPageHero category={category} />}
    <MerchandiseCategoriesCarousel
      title={
        <FormattedMessage
          id="HHfjZIEROrAf_YdZmV-Y"
          defaultMessage="Shop {categoryName} <em>categories</em>"
          description="Heading title for category subcategories carousel in the merchandise shop section"
          values={{
            categoryName: category.name,
            em: (chunks) => <em>{chunks}</em>,
          }}
        />
      }
      overrideFetching={fetchingCategory}
      overrideList={category.childCategoryIds}
    />
    {categoryFilters && <MerchandiseOfferCarousel
      title={
        <FormattedMessage
          id="V68Q2QFwKzIUtwSbTZR4"
          defaultMessage="Top products in <em>{categoryName}</em>"
          description="Heading title for top products in the category carousel"
          values={{
            categoryName: category.name,
            em: (chunks) => <em>{chunks}</em>,
          }}
        />
      }
      filters={categoryFilters}
      viewAllTo={getMerchandiseCategorySearchUrl(category)}
      trackingListName={`Top products in ${category.name}`}
      trackingPageName={trackingPageName}
    />}
    {brandFilters && <MerchandiseBrandsCarousel
      title={
        <FormattedMessage
          id="mqHi3-3NT7gI9DAWll-H"
          defaultMessage="Top {categoryName} <em>brands</em>"
          description="Heading title for top brands in the category carousel"
          values={{
            categoryName: category.name,
            em: (chunks) => <em>{chunks}</em>,
          }}
        />
      }
      filters={brandFilters}
    />}
    {childCategoryElements}
  </div>
}

export default MerchandiseCategoryPage
